import React, { useState } from "react";
import { api } from "../../lib/api";
import { formatDateDisplay, formatPlatform } from "../../lib/utils";
import { toast } from "sonner";
import { ExternalLink, Save, X, Tag, XCircle } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS = ["placed", "processing", "shipped", "delivered", "cancelled"];

const fmt = (v) => (v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function AdminOrderPopup({ order, onClose, onUpdated }) {
  const [status, setStatus] = useState(order.status);
  const [trackingLink, setTrackingLink] = useState(order.tracking_link || "");
  const [adminNotes, setAdminNotes] = useState(order.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  const isPreorder = order.order_type === "preorder";

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.id}`, {
        status,
        tracking_link: trackingLink.trim() || null,
        admin_notes: adminNotes.trim() || null,
      });
      toast.success("Order updated");
      onClose();
      onUpdated();
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const cancelOrder = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.id}`, { status: "cancelled" });
      toast.success("Order cancelled — stock restored");
      setCancelConfirm(false);
      onClose();
      onUpdated();
    } catch {
      toast.error("Cancel failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy-900/80 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-navy-800 rounded-2xl p-6 max-w-xl w-full neon-border max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <div className="text-xs uppercase tracking-wider text-gold">Order</div>
              {isPreorder && (
                <span className="bg-gold/20 text-gold text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded">PRE-ORDER</span>
              )}
            </div>
            <div className="text-2xl font-black">#{order.order_number}</div>
            <div className="text-xs text-slate-400 mt-0.5">{formatDateDisplay(order.created_at)}</div>
            {isPreorder && order.release_date && (
              <div className="text-xs text-gold/70 mt-0.5">Releases: {formatDateDisplay(order.release_date)}</div>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Customer info */}
        <div className="text-sm space-y-1 text-slate-300">
          <div><span className="text-slate-500">Customer:</span> {order.customer_name} · {order.customer_email} · {order.customer_phone}</div>
          {order.shipping_address && (
            <div><span className="text-slate-500">Address:</span> {order.shipping_address}, {order.city}, {order.state} — {order.pincode}</div>
          )}
          <div><span className="text-slate-500">Payment:</span> {order.payment_method} ({order.payment_status})</div>
          {order.razorpay_payment_id && (
            <div className="font-mono text-xs text-slate-400"><span className="text-slate-500">Payment ID:</span> {order.razorpay_payment_id}</div>
          )}
          {order.admin_notes && (
            <div className="mt-1 text-xs text-slate-400 italic">💬 {order.admin_notes}</div>
          )}
        </div>

        {/* Items */}
        <div className="mt-4 border-t border-gold/20 pt-4 space-y-2">
          {(order.items || []).map((it, i) => (
            <Link key={i} to={`/product/${it.product_id}`} target="_blank" rel="noreferrer" className="flex gap-3 text-sm hover:bg-navy-700/60 rounded-lg px-2 py-1 -mx-2 transition">
              {it.image && <img src={it.image} alt="" className="w-12 h-12 object-cover rounded shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold hover:text-gold transition truncate">{it.name}</div>
                <div className="text-xs text-slate-400">
                  Qty {it.quantity} · ₹{it.price.toLocaleString()}
                  {it.platform && <span className="ml-1">· {formatPlatform(it.platform)}</span>}
                  {it.condition && <span className="ml-1">· {it.condition.charAt(0).toUpperCase() + it.condition.slice(1)}</span>}
                </div>
              </div>
              <div className="font-bold shrink-0">₹{(it.price * it.quantity).toLocaleString()}</div>
            </Link>
          ))}
        </div>

        {/* Totals */}
        {isPreorder ? (
          <div className="mt-3 border-t border-gold/20 pt-3 space-y-2 text-sm">
            {order.booking_amount != null && (
              <div className="flex justify-between">
                <span className="text-slate-500">Booking Amount Collected</span>
                <span className="font-bold text-gold">₹{fmt(order.booking_amount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-500">Delivery</span>
              <span>
                {!order.delivery_required
                  ? <span className="text-slate-400">Not required</span>
                  : order.delivery_included
                  ? <span className="text-emerald-400">Required — Included in price</span>
                  : <span className="text-sky-300">Required — Extra charge</span>}
              </span>
            </div>
            <div className="flex justify-between items-baseline border-t border-gold/20 pt-2 mt-1">
              <span className="text-xs uppercase tracking-wider text-slate-400">Total Price</span>
              <span className="text-2xl font-black text-gold">₹{fmt(order.total)}</span>
            </div>
          </div>
        ) : (
          <div className="mt-3 border-t border-gold/20 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>₹{fmt(order.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span>{order.shipping === 0 ? "FREE" : `₹${order.shipping}`}</span></div>
            {(order.processing_fee || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-slate-500">Digital processing (2.5%)</span>
                <span>₹{fmt(order.processing_fee)}</span>
              </div>
            )}
            {order.coupon_code && (
              <div className="flex justify-between text-emerald-400">
                <span className="flex items-center gap-1.5 text-xs"><Tag className="w-3 h-3" /><span className="font-mono font-bold">{order.coupon_code}</span></span>
                <span>−₹{fmt(order.coupon_discount || 0)}</span>
              </div>
            )}
            <div className="flex justify-between items-baseline border-t border-gold/20 pt-2 mt-1">
              <span className="text-xs uppercase tracking-wider text-slate-400">Total</span>
              <span className="text-2xl font-black text-gold">₹{fmt(order.total)}</span>
            </div>
          </div>
        )}

        {/* Fulfillment */}
        <div className="mt-4 border-t border-gold/20 pt-4 space-y-4">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-gold">Fulfillment</div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Order Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60"
            >
              {STATUS.map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Tracking Link</span>
            <div className="flex gap-2 mt-1">
              <input
                value={trackingLink}
                onChange={(e) => setTrackingLink(e.target.value)}
                placeholder="https://bluedart.com/track/..."
                className="flex-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60"
              />
              {trackingLink && (
                <a href={trackingLink} target="_blank" rel="noreferrer" className="p-2 border border-gold/20 rounded-md text-gold hover:border-gold transition">
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Customer Note (shown on Track page)</span>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="e.g. Dispatched via BlueDart, expected delivery 3 May..."
              rows={3}
              className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60 resize-none"
            />
          </label>

          <button
            onClick={saveAll}
            disabled={saving}
            className="w-full bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs py-2.5 rounded-full hover:bg-gold-light transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Saving…" : "Save Changes"}
          </button>

          {/* Cancel order */}
          {order.status !== "cancelled" && (
            <div className="border-t border-rose-500/20 pt-4">
              {!cancelConfirm ? (
                <button
                  onClick={() => setCancelConfirm(true)}
                  className="w-full border border-rose-500/40 text-rose-400 font-bold uppercase tracking-wider text-xs py-2.5 rounded-full hover:bg-rose-500/10 transition flex items-center justify-center gap-2"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  Cancel Order
                </button>
              ) : (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 space-y-3">
                  <p className="text-sm text-rose-300 font-semibold">Are you sure you want to cancel this order?</p>
                  <p className="text-xs text-slate-400">Stock will be restored for all items. This cannot be undone.</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setCancelConfirm(false)}
                      className="flex-1 border border-gold/30 text-slate-300 font-bold uppercase tracking-wider text-xs py-2.5 rounded-full hover:bg-navy-700"
                    >
                      Keep It
                    </button>
                    <button
                      onClick={cancelOrder}
                      disabled={saving}
                      className="flex-1 bg-rose-500 text-white font-extrabold uppercase tracking-wider text-xs py-2.5 rounded-full hover:bg-rose-400 disabled:opacity-50"
                    >
                      {saving ? "Cancelling…" : "Yes, Cancel"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
